import React from 'react';
import {
  BookObservabilityAlert,
  BookObservabilitySnapshot
} from '../types/bookReview';

type MetricState = 'healthy' | 'attention' | 'neutral' | 'unchecked';

interface MetricCardProps {
  id: string;
  title: string;
  value: string;
  detail: string;
  state: MetricState;
}

export interface BookObservabilityDashboardProps {
  observability?: BookObservabilitySnapshot;
}

const finiteNonNegative = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value) && value >= 0
);

const roundCount = (value: number) => Math.round(value).toLocaleString();

const formatPercent = (value: number) => {
  const clamped = Math.min(Math.max(value, 0), 100);
  return `${new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1
  }).format(clamped)}%`;
};

const formatCurrency = (value: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
};

const formatSnapshotTime = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const listParts = (parts: string[]) => parts.filter(Boolean).join(' · ');

const plural = (count: number, singular: string, pluralForm = `${singular}s`) => (
  `${roundCount(count)} ${count === 1 ? singular : pluralForm}`
);

const MetricCard: React.FC<MetricCardProps> = ({ id, title, value, detail, state }) => {
  const statusLabel = state === 'unchecked'
    ? 'Unchecked'
    : state === 'attention'
      ? 'Needs attention'
      : 'Recorded';
  return (
    <article
      className={`book-observability-card ${state}`}
      data-testid={`observability-${id}`}
      data-status={state}
      aria-labelledby={`observability-${id}-title`}
      aria-label={`${title}. ${statusLabel}. ${value}. ${detail}`}
    >
      <header>
        <h4 id={`observability-${id}-title`}>{title}</h4>
        <span className={`book-observability-state ${state}`}>
          <span aria-hidden="true">{state === 'unchecked' ? '?' : state === 'attention' ? '!' : '✓'}</span>
          {statusLabel}
        </span>
      </header>
      <strong className="book-observability-value" data-testid={`observability-${id}-value`}>
        {value}
      </strong>
      <p>{detail}</p>
    </article>
  );
};

const AlertItem: React.FC<{ alert: BookObservabilityAlert }> = ({ alert }) => (
  <li
    className={`book-observability-alert ${alert.severity}`}
    data-alert-code={alert.code}
    data-alert-severity={alert.severity}
  >
    <span aria-hidden="true">{alert.severity === 'error' ? '!' : alert.severity === 'warning' ? '△' : 'i'}</span>
    <div>
      <strong>{alert.title}</strong>
      <p>{alert.message}</p>
      <small>
        {alert.code}
        {finiteNonNegative(alert.affectedOperations)
          ? ` · ${plural(alert.affectedOperations, 'affected operation')}`
          : ''}
      </small>
    </div>
  </li>
);

const BookObservabilityDashboard: React.FC<BookObservabilityDashboardProps> = ({ observability }) => {
  const recorded = observability?.coverage === 'recorded';

  const generation = observability?.generation;
  const generationRecorded = (generation?.coverage || observability?.coverage) === 'recorded';
  const generationAvailable = recorded
    && generationRecorded
    && finiteNonNegative(generation?.logicalOperations)
    && finiteNonNegative(generation?.succeeded)
    && finiteNonNegative(generation?.failed);
  const generationOperations = generationAvailable && generation ? generation.logicalOperations! : 0;
  const generationSucceeded = generationAvailable && generation ? generation.succeeded! : 0;
  const generationFailed = generationAvailable && generation ? generation.failed! : 0;
  const generationRetried = generationAvailable && generation && finiteNonNegative(generation.retried)
    ? generation.retried
    : 0;
  const generationSuccessRate = generationAvailable && generation && finiteNonNegative(generation.successRatePercent)
    ? generation.successRatePercent
    : generation?.successRatePercent === undefined && generationOperations > 0
      ? generationSucceeded / generationOperations * 100
      : null;

  const consistency = observability?.consistency;
  const consistencyRecorded = (consistency?.coverage || observability?.coverage) === 'recorded';
  const consistencyAvailable = recorded
    && consistencyRecorded
    && finiteNonNegative(consistency?.checked)
    && finiteNonNegative(consistency?.passed);
  const consistencyChecked = consistencyAvailable && consistency ? consistency.checked! : 0;
  const consistencyPassed = consistencyAvailable && consistency ? consistency.passed! : 0;
  const consistencyFailed = consistencyAvailable && consistency && finiteNonNegative(consistency.failed)
    ? consistency.failed
    : Math.max(consistencyChecked - consistencyPassed, 0);
  const consistencyScore = consistencyAvailable && consistency && finiteNonNegative(consistency.scorePercent)
    ? consistency.scorePercent
    : consistency?.scorePercent === undefined && consistencyChecked > 0
      ? consistencyPassed / consistencyChecked * 100
      : null;

  const exportMetric = observability?.export;
  const exportRecorded = (exportMetric?.coverage || observability?.coverage) === 'recorded';
  const exportAvailable = recorded
    && exportRecorded
    && finiteNonNegative(exportMetric?.attempts)
    && finiteNonNegative(exportMetric?.passed)
    && finiteNonNegative(exportMetric?.blocked);
  const exportAttempts = exportAvailable && exportMetric ? exportMetric.attempts! : 0;
  const exportPassed = exportAvailable && exportMetric ? exportMetric.passed! : 0;
  const exportFailures = exportAvailable && exportMetric && finiteNonNegative(exportMetric.failed)
    ? exportMetric.failed
    : exportAvailable && exportMetric
      ? exportMetric.blocked!
      : 0;

  const cost = observability?.cost;
  const costCoverage = cost?.coverage || observability?.coverage;
  const costScope = cost?.scope || 'completed-book';
  const costTitle = costScope === 'cost-so-far' ? 'Cost so far' : 'Completed-book cost';
  const costSubject = costScope === 'cost-so-far' ? 'this book so far' : 'this completed book';
  const completedBookCost = cost?.completedBookUsd;
  const customerTokens = cost?.customerTokens;
  const costCurrency = (cost?.currency || 'USD').toUpperCase();
  const providerCostAvailable = recorded
    && costCoverage === 'recorded'
    && finiteNonNegative(completedBookCost);
  const tokenCostAvailable = recorded
    && costCoverage === 'recorded'
    && finiteNonNegative(customerTokens);
  const costAvailable = providerCostAvailable || tokenCostAvailable;

  const alerts: BookObservabilityAlert[] = Array.isArray(observability?.alerts)
    ? observability?.alerts || []
    : [];
  const retention = observability?.retention;
  const operationDetailsDays = retention?.operationDetailsDays;
  const aggregateMetricsDays = retention?.aggregateMetricsDays;
  const proofArtifactsHours = retention?.proofArtifactsHours;
  const retentionItems = [
    finiteNonNegative(operationDetailsDays)
      ? `${plural(operationDetailsDays, 'day')} operation details`
      : '',
    finiteNonNegative(aggregateMetricsDays)
      ? `${plural(aggregateMetricsDays, 'day')} aggregate metrics`
      : '',
    finiteNonNegative(proofArtifactsHours)
      ? `${plural(proofArtifactsHours, 'hour')} proof artifacts`
      : ''
  ].filter(Boolean);
  const snapshotTime = formatSnapshotTime(observability?.generatedAt);

  const generationCard: MetricCardProps = generationAvailable
    ? {
        id: 'generation',
        title: 'Generation success',
        value: generationOperations === 0 || generationSuccessRate === null
          ? 'No operations'
          : formatPercent(generationSuccessRate),
        detail: generationOperations === 0
          ? 'No generation operations have been recorded for this book.'
          : listParts([
              `${plural(generationSucceeded, 'success', 'successes')}`,
              `${plural(generationFailed, 'failure')}`,
              `${plural(generationRetried, 'retry', 'retries')}`
            ]),
        state: generationFailed > 0 ? 'attention' : generationOperations > 0 ? 'healthy' : 'neutral'
      }
    : {
        id: 'generation',
        title: 'Generation success',
        value: 'Unchecked',
        detail: 'Generation outcomes were not recorded for this book.',
        state: 'unchecked'
      };

  const consistencyCard: MetricCardProps = consistencyAvailable
    ? {
        id: 'consistency',
        title: 'Consistency failures',
        value: consistencyChecked === 0
          ? 'Not run'
          : plural(consistencyFailed, 'failure'),
        detail: consistencyChecked === 0
          ? 'No automated character consistency checks have run yet.'
          : listParts([
              `${plural(consistencyChecked, 'check')}`,
              consistencyScore === null ? '' : `${formatPercent(consistencyScore)} passed`
            ]),
        state: consistencyFailed > 0 ? 'attention' : consistencyChecked > 0 ? 'healthy' : 'neutral'
      }
    : {
        id: 'consistency',
        title: 'Consistency failures',
        value: 'Unchecked',
        detail: 'Consistency history is unavailable; review references manually.',
        state: 'unchecked'
      };

  const exportCard: MetricCardProps = exportAvailable
    ? {
        id: 'export',
        title: 'Export failures',
        value: exportAttempts === 0
          ? 'No attempts'
          : plural(exportFailures, 'failure'),
        detail: exportAttempts === 0
          ? 'No authoritative export attempts have been recorded yet.'
          : listParts([
              plural(exportAttempts, 'attempt'),
              `${plural(exportPassed, 'proof')} ready`,
              `${plural(exportMetric!.blocked!, 'blocked export')}`
            ]),
        state: exportFailures > 0 ? 'attention' : exportAttempts > 0 ? 'healthy' : 'neutral'
      }
    : {
        id: 'export',
        title: 'Export failures',
        value: 'Unchecked',
        detail: 'Authoritative export outcomes were not recorded for this book.',
        state: 'unchecked'
      };

  const costCard: MetricCardProps = costAvailable
    ? {
        id: 'cost',
        title: costTitle,
        value: providerCostAvailable
          ? formatCurrency(completedBookCost!, costCurrency)
          : `${roundCount(customerTokens!)} tokens`,
        detail: providerCostAvailable
          ? `Recorded provider cost attributed to ${costSubject}.`
          : `Recorded customer tokens attributed to ${costSubject}; provider USD cost is unavailable.`,
        state: 'neutral'
      }
    : {
        id: 'cost',
        title: costTitle,
        value: 'Unavailable',
        detail: 'Cost attribution is incomplete, so no estimate is shown.',
        state: 'unchecked'
      };

  return (
    <section
      className="book-observability-dashboard"
      data-testid="book-observability-dashboard"
      data-coverage={recorded ? 'recorded' : 'unchecked'}
      aria-labelledby="book-observability-title"
    >
      <header className="book-observability-header">
        <div>
          <p className="book-observability-eyebrow">Operations & cost</p>
          <h3 id="book-observability-title">Book health at a glance</h3>
          <p>Privacy-safe results from recorded generation, consistency, and export operations.</p>
        </div>
        {snapshotTime && <p className="book-observability-updated">Updated {snapshotTime}</p>}
      </header>

      {!recorded && (
        <div className="book-observability-legacy" role="note" data-testid="observability-legacy-notice">
          <span aria-hidden="true">?</span>
          <p>
            <strong>Historical metrics are unavailable.</strong>{' '}
            This legacy book is shown as Unchecked—missing records are never treated as zero failures.
          </p>
        </div>
      )}

      <div className="book-observability-grid" aria-label="Book operation metrics">
        <MetricCard {...generationCard} />
        <MetricCard {...consistencyCard} />
        <MetricCard {...exportCard} />
        <MetricCard {...costCard} />
      </div>

      <div className="book-observability-details">
        <section aria-labelledby="book-observability-alerts-title">
          <h4 id="book-observability-alerts-title">Operational alerts</h4>
          {alerts.length > 0 ? (
            <ul className="book-observability-alerts" data-testid="observability-alerts">
              {alerts.map((alert, index) => (
                <AlertItem key={`${alert.code}-${index}`} alert={alert} />
              ))}
            </ul>
          ) : (
            <p className="book-observability-empty-detail" data-testid="observability-alerts-empty">
              {recorded
                ? 'No active alerts were recorded for this snapshot.'
                : 'Alerts are unchecked because operation history is unavailable.'}
            </p>
          )}
        </section>

        <section aria-labelledby="book-observability-retention-title">
          <h4 id="book-observability-retention-title">Privacy & retention</h4>
          <p className="book-observability-privacy">
            This view uses operational counts and hashes; it does not display raw prompts or book text.
          </p>
          {retentionItems.length > 0 ? (
            <p className="book-observability-retention" data-testid="observability-retention">
              Retained for {retentionItems.join('; ')}.
              {retention?.policyVersion ? ` Policy ${retention.policyVersion}.` : ''}
            </p>
          ) : (
            <p className="book-observability-empty-detail" data-testid="observability-retention-unavailable">
              Retention details are unavailable for this snapshot.
            </p>
          )}
        </section>
      </div>
    </section>
  );
};

export default BookObservabilityDashboard;
